import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { downloadBlob } from "@/lib/download";

const money = (n) => (n == null ? "" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const monthLabel = (m) => { try { return new Date(`${m}-01T00:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" }); } catch { return m; } };

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [13, 148, 136];
};

const loadImageDataUrl = (url) => new Promise((resolve) => {
  if (!url) return resolve(null);
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

export const exportStatementExcel = (data, brand = {}) => {
  const rows = data.deals.map((d) => ({
    Client: d.name, Company: d.company || "", "Won on": (d.won_at || "").slice(0, 10),
    Premium: Number(d.premium), "Commission %": d.commission_pct, Commission: Number(d.commission),
  }));
  rows.push({});
  rows.push({ Client: "TOTAL", Premium: Number(data.total_premium), Commission: Number(data.total_commission) });
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Commission");
  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  downloadBlob(out, `commission-statement-${data.month}.xlsx`,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
};

const brandedHeader = async (doc, brand, subtitle, title) => {
  const [r, g, b] = hexToRgb(brand.color);
  const pw = doc.internal.pageSize.getWidth();
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, pw, 26, "F");
  const logo = await loadImageDataUrl(brand.logoUrl);
  let tx = 14;
  if (logo) {
    const h = 14, w = Math.min(28, (logo.w / logo.h) * h);
    try { doc.addImage(logo.dataUrl, "PNG", 12, 6, w, h); tx = 12 + w + 6; } catch { /* ignore */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.text(title, tx, 12);
  doc.setFontSize(9);
  doc.text(subtitle, tx, 20);
  return [r, g, b];
};

export const exportStatementPDF = async (data, brand = {}) => {
  const name = brand.name || "IShareLeads";
  const doc = new jsPDF();
  const [r, g, b] = await brandedHeader(doc, brand,
    `${data.agent_name || ""} · ${monthLabel(data.month)}${brand.address ? " · " + brand.address : ""}`,
    `${name} — Commission Statement`);
  autoTable(doc, {
    startY: 32,
    head: [["Client", "Company", "Won on", "Premium", "Comm %", "Commission"]],
    body: data.deals.map((d) => [d.name || "", d.company || "", (d.won_at || "").slice(0, 10), money(d.premium), `${d.commission_pct || 0}%`, money(d.commission)]),
    foot: [["", "", "Totals", money(data.total_premium), "", money(data.total_commission)]],
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [r, g, b] },
    footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: "bold" },
  });
  const y = doc.lastAutoTable.finalY || 32;
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(9);
  doc.text(`${data.count} won deal(s) · Generated ${new Date().toLocaleString("en-US")}`, 14, Math.min(y + 8, doc.internal.pageSize.getHeight() - 8));
  downloadBlob(doc.output("blob"), `commission-statement-${data.month}.pdf`, "application/pdf");
};

export const exportTeamReportExcel = (data, brand = {}) => {
  const rows = data.rows.map((a) => ({
    Agent: a.name, "Won deals": a.deals, Premium: Number(a.premium), Commission: Number(a.commission),
  }));
  rows.push({});
  rows.push({ Agent: "TOTAL", "Won deals": data.total_deals, Premium: Number(data.total_premium), Commission: Number(data.total_commission) });
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Team commission");
  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  downloadBlob(out, `team-commission-${data.month}.xlsx`,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
};

export const exportTeamReportPDF = async (data, brand = {}) => {
  const name = brand.name || data.org_name || "IShareLeads";
  const doc = new jsPDF();
  const [r, g, b] = await brandedHeader(doc, brand,
    `Team commission · ${monthLabel(data.month)}${brand.address ? " · " + brand.address : ""}`,
    `${name} — Team Commission Report`);
  autoTable(doc, {
    startY: 32,
    head: [["Agent", "Won deals", "Premium", "Commission"]],
    body: data.rows.map((a) => [a.name || "", String(a.deals), money(a.premium), money(a.commission)]),
    foot: [["Totals", String(data.total_deals), money(data.total_premium), money(data.total_commission)]],
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [r, g, b] },
    footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: "bold" },
  });
  const y = doc.lastAutoTable.finalY || 32;
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(9);
  doc.text(`${data.agents} agent(s) · Generated ${new Date().toLocaleString("en-US")}`, 14, Math.min(y + 8, doc.internal.pageSize.getHeight() - 8));
  downloadBlob(doc.output("blob"), `team-commission-${data.month}.pdf`, "application/pdf");
};
